import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type { ProductStatsParams, ProductStatsResponse } from '../types';

/**
 * Fetch product statistics for a household.
 */
async function getProductStats(
  householdId: string,
  params?: ProductStatsParams
): Promise<ProductStatsResponse> {
  const searchParams = new URLSearchParams();

  if (params?.from) searchParams.set('from', params.from);
  if (params?.to) searchParams.set('to', params.to);
  if (params?.currentOnly !== undefined)
    searchParams.set('currentOnly', String(params.currentOnly));
  if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params?.order) searchParams.set('order', params.order);

  const query = searchParams.toString();
  const url = `/households/${householdId}/items/product-stats${query ? `?${query}` : ''}`;
  return apiFetch<ProductStatsResponse>(url);
}

/**
 * Hook to fetch product statistics for a household.
 *
 * Returns consumption, waste, and spending statistics aggregated by product.
 *
 * @param householdId - Household UUID
 * @param params - Optional query parameters for filtering and sorting
 * @returns Query result with product statistics
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useProductStats(householdId, {
 *   sortBy: 'wasteRate',
 *   order: 'desc',
 * });
 * ```
 */
export function useProductStats(householdId: string, params?: ProductStatsParams) {
  return useQuery({
    queryKey: queryKeys.items.productStats(householdId, params),
    queryFn: () => getProductStats(householdId, params),
    enabled: !!householdId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}
